import { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { GENERATION_REFERENCE_MIME_TYPES, isGenerationReferenceMime } from '@mg/shared';
import type { ReferenceRole, ReferenceRoleId } from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Button } from '../../components/ui/Button';
import { useProxyImage } from '../../lib/useProxyImage';

/** One selected reference. Library picks and uploads share one ordered list, in selection order. */
export interface MediaReference {
  assetId?: string;
  name: string;
  /**
   * Proxy URL for DISPLAY ONLY — the thumbnail when the asset has one.
   *
   * The card draws this at 80px and takes up to five, so pointing it at the original meant five
   * full-resolution downloads to paint five small squares. Safe because the server never reads this:
   * references travel as `assetId` and are resolved to bytes server-side.
   */
  url: string;
  /**
   * Which named slot this fills, for models whose references are not interchangeable.
   *
   * Carried on the image rather than implied by its index: with a positional convention, filling
   * only the second slot would send one reference that the server reads as the first, and the model
   * would build a confident image from the wrong input.
   */
  role?: ReferenceRoleId;
}

const MAX_BYTES = 100 * 1024 * 1024;

/**
 * Reference images for a generation, using the post composer's IMAGES card shape so the two feel
 * like one component.
 *
 * Everything selected is written to the Media Library first, so each reference has a stable URL —
 * providers that fetch references server-side (Replicate, Fal) cannot read bytes we only hold in
 * the browser, and express's 100 kb body limit rules out sending them inline as base64 anyway.
 */
export function MediaReferenceCard({
  refs,
  max,
  onChange,
  disabledReason,
  onBrowseLibrary,
  uploadSignal,
  roles,
  minRequired = 0,
}: {
  refs: MediaReference[];
  max: number;
  onChange: (next: MediaReference[]) => void;
  /** When set, the whole card is inert and explains why. */
  disabledReason?: string;
  /** Opens the library browser over the preview pane (the modal owns that surface). */
  onBrowseLibrary?: (role?: ReferenceRoleId) => void;
  /** Incremented by the overlay's Upload button to open this card's file picker. */
  uploadSignal?: number;
  /**
   * Named slots instead of a flat list. When set, the card renders one target per role rather than
   * a grid, because these images are not interchangeable and a grid would imply they are.
   */
  roles?: ReferenceRole[];
  /**
   * How many references this model REQUIRES. Shown in the card rather than only enforced on submit:
   * every "(with references)" model refused the request after the click, which is late enough that
   * the user has already typed a prompt and chosen a ratio.
   */
  minRequired?: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  /** Which slot an upload started from, so the resulting file lands in the right role. */
  const pendingRole = useRef<ReferenceRoleId | undefined>(undefined);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The overlay's Upload button has no file input of its own; it nudges this one, which already
  // owns the size checks and the write-to-library step.
  useEffect(() => {
    if (uploadSignal) inputRef.current?.click();
  }, [uploadSignal]);

  const disabled = Boolean(disabledReason) || max === 0;
  const atLimit = refs.length >= max;
  /** Requirement not yet met — stated up front rather than discovered on submit. */
  const unmet = minRequired > 0 && refs.length < minRequired;

  const addFiles = async (files: File[]) => {
    setError(null);
    // Same allowlist the library picker uses — `startsWith('image/')` accepted SVG, which no image
    // model can read, so a dropped Figma export uploaded fine and then produced nothing.
    const images = files.filter((f) => isGenerationReferenceMime(f.type));
    if (images.length < files.length) {
      setError('References must be JPG or PNG.');
      if (images.length === 0) return;
    }
    const room = max - refs.length;
    if (room <= 0) return;
    const accepted = images.slice(0, room);
    const tooBig = accepted.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      setError(`${tooBig.name} is over 100MB.`);
      return;
    }
    setBusy(true);
    try {
      const added: MediaReference[] = [];
      for (const file of accepted) {
        const asset = await api.uploadAsset(file);
        added.push({ assetId: asset.id, name: asset.name ?? file.name, url: asset.thumbUrl ?? asset.blobUrl });
      }
      const role = pendingRole.current;
      if (role) {
        // One image per slot: an upload REPLACES whatever was there rather than appending, which a
        // flat concat would do and would then exceed the slot count.
        const [first] = added;
        onChange([...refs.filter((r) => r.role !== role), ...(first ? [{ ...first, role }] : [])]);
        pendingRole.current = undefined;
      } else {
        onChange([...refs, ...added]);
      }
    } catch (err) {
      setError(actionErrorText(err, 'Could not upload that file.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('rounded-lg border border-line', disabled && 'opacity-60')}>
      <div className="flex items-center gap-2 border-b border-line-soft px-3 py-2">
        <ImageIcon className="h-4 w-4 text-ink-faint" aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-label">
          Media reference
        </span>
        <span className="ml-auto text-xs text-ink-faint">
          {roles ? `${refs.length} of ${roles.length} slots` : `${refs.length} of ${max || 0}`}
        </span>
      </div>

      <div className="space-y-2 px-3 py-3">
        {disabled ? (
          <p className="text-xs text-ink-muted">{disabledReason ?? 'Not available for this model.'}</p>
        ) : roles ? (
          <div className="space-y-3">
            {unmet && (
              <p className="rounded-md bg-amber-50 dark:bg-warning-dark-tint px-2.5 py-1.5 text-xs text-amber-800 dark:text-warning-dark">
                {minRequired === 1
                  ? 'At least one reference is required for this model.'
                  : `At least ${minRequired} references are required for this model.`}
              </p>
            )}

            {roles.map((role) => {
              const filled = refs.find((r) => r.role === role.id);
              return (
                <div key={role.id}>
                  <p className="text-xs font-semibold text-ink-strong">{role.label}</p>
                  <p className="mb-1.5 text-[0.6875rem] leading-tight text-ink-faint">{role.hint}</p>
                  <div className="flex items-start gap-2">
                    {filled ? (
                      <ReferenceThumb
                        reference={filled}
                        onRemove={() => onChange(refs.filter((r) => r.role !== role.id))}
                      />
                    ) : (
                      <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-line-dashed text-[0.625rem] text-ink-faint">
                        Empty
                      </span>
                    )}
                    <div className="flex flex-col gap-1.5">
                      {onBrowseLibrary && (
                        <Button size="sm" variant="secondary" onClick={() => onBrowseLibrary(role.id)}>
                          <ImageIcon className="h-4 w-4" aria-hidden />
                          Library
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => {
                          pendingRole.current = role.id;
                          inputRef.current?.click();
                        }}
                      >
                        <Upload className="h-4 w-4" aria-hidden />
                        Upload
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            <input
              ref={inputRef}
              type="file"
              accept={GENERATION_REFERENCE_MIME_TYPES.join(',')}
              className="hidden"
              onChange={(e) => {
                void addFiles(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
            <p className="text-xs text-ink-faint">JPG or PNG &middot; up to 100MB</p>
            {error && <p className="text-xs text-red-600 dark:text-error-dark">{error}</p>}
          </div>
        ) : (
          <>
            {unmet && (
              <p className="rounded-md bg-amber-50 dark:bg-warning-dark-tint px-2.5 py-1.5 text-xs text-amber-800 dark:text-warning-dark">
                {minRequired === 1
                  ? 'At least one reference is required for this model.'
                  : `At least ${minRequired} references are required for this model.`}
              </p>
            )}
            {/*
              Capped, and scrolls internally past the cap.

              This card is the one thing in the form column that grows with user input, and an
              unbounded grower inside a scrolling column is what pushed the Cancel/Generate bar out
              of the panel. Five references plus the drop tile is at most two rows at this width, so
              the cap is invisible in normal use and simply removes the failure mode — the card can
              no longer be the reason the column doesn't fit.
            */}
            <div className="flex max-h-[11.5rem] flex-wrap gap-2 overflow-y-auto">
              {/* The drop tile disappears at the cap — there is nothing more it could accept. */}
              {!atLimit && (
                <label
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    void addFiles(Array.from(e.dataTransfer.files));
                  }}
                  className={cn(
                    'flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-1 text-center text-[0.625rem] leading-tight',
                    dragOver ? 'border-action bg-tint text-tint-fg' : 'border-line-dashed text-ink-muted',
                  )}
                >
                  <Upload className="h-4 w-4" aria-hidden />
                  Drag and Drop Images to Upload
                  <input
                    ref={inputRef}
                    type="file"
                    accept={GENERATION_REFERENCE_MIME_TYPES.join(',')}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      void addFiles(Array.from(e.target.files ?? []));
                      e.target.value = '';
                    }}
                  />
                </label>
              )}

              {refs.map((ref, i) => (
                <ReferenceThumb
                  key={`${ref.assetId ?? ref.name}-${i}`}
                  reference={ref}
                  onRemove={() => onChange(refs.filter((_, idx) => idx !== i))}
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {onBrowseLibrary && (
                <Button size="sm" disabled={atLimit} onClick={() => onBrowseLibrary()}>
                  <ImageIcon className="h-4 w-4" aria-hidden />
                  Add from Media Library
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={atLimit || busy}
                onClick={() => inputRef.current?.click()}
                loading={busy}
              >
                <Upload className="h-4 w-4" aria-hidden />
                Upload
              </Button>
              <span className="text-xs text-ink-faint">JPG or PNG</span>
              <span className="text-xs text-ink-faint">Up to 100MB</span>
            </div>
            {error && <p className="text-xs text-red-600 dark:text-error-dark">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}

function ReferenceThumb({
  reference,
  onRemove,
}: {
  reference: MediaReference;
  onRemove: () => void;
}) {
  // Library assets are served through the bearer-guarded proxy, so a bare <img src> would 401.
  const src = useProxyImage(reference.url);
  return (
    <span className="relative h-20 w-20 overflow-hidden rounded-lg border border-line">
      {src ? (
        <img src={src} alt={reference.name} className="h-full w-full bg-white object-cover" />
      ) : (
        <span className="block h-full w-full animate-pulse bg-chip" />
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${reference.name}`}
        className="focus-ring absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white hover:bg-black"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}
